import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { ROUTES } from '../../contracts/routes';
import LogoSpinner from '../oracle/LogoSpinner';
import UserDocRecovery from './UserDocRecovery';

// Auth + onboarding chokepoint for every protected route.
//
//   request route
//      │ userLoggedIn? ── no ──> Navigate SIGN_IN (state.from = dest)
//      │ yes
//      │ loading? ── yes ──> <LogoSpinner/>
//      │ no
//      │ userData === null? ── yes ──> <UserDocRecovery/>   (resolved + MISSING)
//      │ no
//      │ onboarding_complete?
//      │   ├ NO  ── on /onboarding? ── yes ──> render children
//      │   │                          no  ──> Navigate ONBOARDING (state.from = dest)
//      │   └ YES ── on /onboarding? ── yes ──> Navigate CREATE (don't re-run)
//      │                              no  ──> render children
//
// THREE states, not two. This used to be `if (loading || userData === null)` →
// spinner, which conflated "still fetching" with "fetched, and there is no doc".
// fetchUserData already calls back with null on !exists and UserContext then
// sets loading=false, so the two ARE distinguishable — the gate just ignored it.
// The cost of conflating them: ~100 accounts whose client-side doc write never
// landed sat on a full-screen spinner forever, with no path forward. They were
// not "stranded mid-funnel"; the app was unusable for them permanently.
//
// UserDocRecovery keeps the brief post-signup grace window (so this is still not
// a flash-redirect) and then actually repairs the doc.
const AuthGate = ({ children }) => {
  const { userLoggedIn } = useAuth();
  const { userData, loading } = useUser();
  const location = useLocation();
  const dest = location.pathname + location.search;
  const onOnboarding = location.pathname === ROUTES.ONBOARDING;

  if (!userLoggedIn) {
    return <Navigate to={ROUTES.SIGN_IN} state={{ from: dest }} replace />;
  }

  // Still fetching.
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LogoSpinner />
      </div>
    );
  }

  // Resolved, and there is no user doc. Recoverable — never a dead spinner.
  if (userData === null) {
    return <UserDocRecovery />;
  }

  if (!userData.onboarding_complete) {
    return onOnboarding ? (
      children
    ) : (
      <Navigate to={ROUTES.ONBOARDING} state={{ from: dest }} replace />
    );
  }

  // Onboarding already done — keep finished users out of the wizard.
  if (onOnboarding) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return children;
};

export default AuthGate;
